import React, { useState } from "react";
import { Button, Input, Spinner } from "@components";
import { AuthenticationService } from "@authentication/AuthenticationService";
import { selectDistributor } from "@authentication/selectDistributor";
import { useNavigate } from "react-router-dom";
import { detectPartnerFromHost } from "@partner/detect";
import { isValidEmail } from "@utils/validators";

export const LoginPage: React.FC = () => {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [generalError, setGeneralError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const navigate = useNavigate();
  const authenticationService = new AuthenticationService();
  const partnerPreset = detectPartnerFromHost();


  /* Login Main Function */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);

    const validation = validateForm();
    setFormErrors(validation.emailError, validation.passwordError);

    if (!validation.isValid) return;

    try {
      clearFormErrors();

      // Step 1: Login → no-scope token
      const loginResponse = await authenticationService.userLogin(email, password);
      if (!loginResponse?.token) {
        setGeneralError("Invalid email or password");
        return;
      }
      authenticationService.saveToken(loginResponse.token);
      authenticationService.saveEmail(email);

      // Step 2: Fetch distributors available to this user
      const distributors = await authenticationService.getDistributorsForUser();

      if (!distributors || distributors.length === 0) {
        authenticationService.clearToken();
        setGeneralError("No distributor access for this account.");
        return;
      }

      // Step 3a: Multiple distributors → go to selection page
      if (distributors.length > 1) {
        navigate("/distributor-select", {
          state: { distributorAccesses: distributors },
        });
        return;
      }

      // Step 3b: Single distributor → auto-select
      await selectDistributor(distributors[0]);
      navigate("/dashboard");

    } catch (error: any) {
      // Don't leave a half-finished login's token behind: ProtectedRoute only checks that a
      // token exists, so a stray no-scope token would let /dashboard load and fail every call.
      authenticationService.clearToken();
      // Only a rejected login means bad credentials; a 500 or a dropped connection must not
      // send the user off to reset a password that is fine.
      const status = error?.response?.status;
      setGeneralError(
        status === 401 || status === 400
          ? "Invalid email or password. Please try again."
          : "We couldn't sign you in right now. Please try again in a moment."
      );
      console.error("Login error:", error);
    } finally {
      setIsLoggingIn(false);
    }
  };


  // FORM VALIDATION
  const validateForm = (): {
    isValid: boolean;
    emailError: string;
    passwordError: string;
  } => {
    const emailError = validateEmail(email);
    const passwordError = !password ? "Password is required" : "";
    setIsLoggingIn(false);

    return {
      isValid: !emailError && !passwordError,
      emailError,
      passwordError,
    };
  };

  const validateEmail = (email: string): string => {
    if (!email) return "Email is required";

    if (!isValidEmail(email)) {
      setIsLoggingIn(false);
      return "Please enter a valid email address";
    }

    return "";
  };

  const setFormErrors = (emailError: string, passwordError: string) => {
    setEmailError(emailError);
    setPasswordError(passwordError);
  };

  const clearFormErrors = () => {
    setEmailError("");
    setPasswordError("");
    setGeneralError("");
  };

  // HANDLERS
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearFormErrors();
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearFormErrors();
    setPassword(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin(e as any);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="w-2/5 relative flex items-start justify-start p-8 bg-cover bg-left"
        style={{ backgroundImage: `url(${partnerPreset.loginImgHref})` }}
      ></div>

      {/* Right Side - 60% */}
      <div className="w-3/5 flex items-center justify-center bg-gray-50 p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="flex flex-col items-center">
            <img
              src={partnerPreset.logoHref}
              alt="Logo"
              className="w-auto h-10 object-cover mb-6"
            />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600">Sign in to your account to continue</p>
          </div>

          <form
            onSubmit={handleLogin}
            className="flex flex-col gap-4 items-center"
          >
            <div className="w-full">
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                error={emailError}
                required
              />
            </div>
            <div className="w-full">
              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                onKeyDown={handleKeyDown}
                error={passwordError}
                required
              />
            </div>

            {/* General Error Display */}
            {generalError && (
              <div className="bg-dangerLight border border-dangerMedium text-danger px-4 py-3 rounded-lg text-sm w-full text-center">
                {generalError}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full mt-4"
              tabIndex={0}
              aria-label="Sign in button"
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleLogin(e as any);
                }
              }}
              disabled={isLoggingIn}
            >
              {!isLoggingIn && "Log in"}
              {isLoggingIn && "Logging you in"}
              {isLoggingIn && <Spinner className="ml-2" />}
            </Button>

            <div className="text-sm">
              <button
                type="button"
                className="font-medium text-black hover:text-slate-700 focus:outline-none focus:underline"
                tabIndex={0}
                onClick={() => {
                  navigate("/forgot-password");
                }}
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
